import { ErrorBoundary } from '@praxis/component-logging'
import { useEnv, withEnv } from '@praxis/component-runtime-env'
import { ProtectedElement, useAuth, withAuth } from '@praxis/component-auth'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { ErrorContent } from '../globalComponents/ErrorContent'
import { LayoutPlaceholder } from './LayoutPlaceholder'
import { ExternalUnauthorizedPage } from './ExternalUnauthorizedPage'
import { RFI_TYPE_LOCATION } from '../common/commonConstants'
import { getRfiTagValues } from '../common/common'
import { getUserPermissions } from '../services/lcmService'
import { logErrorApiCalls } from '../utils/httpUtils'
import { useState, useEffect } from 'react'
import { func, array } from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators, compose } from 'redux'
import { updateUserSettings } from '../actions/action'
import {
  SpinnerComponent,
  UnauthorizedPage,
  handleBrowserReroute,
} from 'mvscommoncomponents'
import RFILanding from '../views/rfi/RFILanding'
import RFI3Landing from '../views/rfi/RFI3Landing'
import VettingSummaryLanding from './Vetting/VettingSummaryLanding'

export const MainRouter = (props) => {
  const location = useLocation()
  const env = useEnv()
  const auth = useAuth()
  const { logout } = auth
  const [isAdminSettingsFetched, setAdminSettingFetched] = useState(false)

  useEffect(() => {
    const getUserAllPermissions = async () => {
      try {
        setAdminSettingFetched(false)
        let userPermissions = await getUserPermissions(
          auth.session.userInfo.memberOf,
        )
        props.updateUserSettings(userPermissions)
        setAdminSettingFetched(true)
      } catch (error) {
        props.updateUserSettings([]) //Incase of failure show all controls
        setAdminSettingFetched(true)
        logErrorApiCalls(error)
      }
    }
    getUserAllPermissions()
  }, [auth]) // eslint-disable-line react-hooks/exhaustive-deps

  handleBrowserReroute(location, 'lcm')

  return (
    <ErrorBoundary key={location.pathname} FallbackComponent={ErrorContent}>
      {isAdminSettingsFetched ? (
        <Routes location={location}>
          <Route
            exact
            index
            path="/" // Using default root landing page as vetting summary
            element={
              <ProtectedElement
                renderUnauthorized={() => (
                  <Navigate to="/unauthorized" replace={true} />
                )}
              >
                <VettingSummaryLanding
                  showSideNav={true}
                  userSettings={props.userSettings}
                />
              </ProtectedElement>
            }
          ></Route>
          <Route
            exact
            path="/externalUnauthorized"
            element={<ExternalUnauthorizedPage />}
          />
          <Route
            index
            path="/unauthorized"
            element={
              <UnauthorizedPage
                accessMessageList={[
                  getRfiTagValues('unAuthorisedMessage')['generic'].replace(
                    'GENERIC_AD_GROUP',
                    env.mfelcm.mvs_rfi_ad_group?.join(' or '),
                  ),
                ]}
                previousPagePath="/lcm"
                logout={() => logout()}
              />
            }
          ></Route>
          <Route
            path="/rfi1"
            element={
              <ProtectedElement
                allowed={env.mfelcm.mvs_rfi_ad_group}
                renderUnauthorized={() => (
                  <Navigate to="/externalUnauthorized" replace={true} />
                )}
              >
                <LayoutPlaceholder
                  component={<RFILanding isReadonly={false} />}
                  isReadonly={false}
                  headerText={'Add Owned Brand Capabilities (RFI 1)'}
                  showSideNav
                />
              </ProtectedElement>
            }
          />
          <Route
            index
            path="/rfi2"
            element={
              <ProtectedElement
                allowed={env.mfelcm.mvs_rfi_ad_group}
                renderUnauthorized={() => (
                  <Navigate to="/externalUnauthorized" replace={true} />
                )}
              >
                <LayoutPlaceholder
                  component={
                    <RFILanding
                      isReadonly={false}
                      rfiType={RFI_TYPE_LOCATION}
                    />
                  }
                  isReadonly={false}
                  headerText={'Add Manufacturing Details (RFI 2)'}
                  showSideNav
                />
              </ProtectedElement>
            }
          />
          <Route
            path="internal/vetting_summary"
            element={
              <ProtectedElement
                allowed={env.mfelcm.mvs_rfi_ad_group}
                renderUnauthorized={() => (
                  <Navigate to="/unauthorized" replace={true} />
                )}
              >
                <VettingSummaryLanding
                  showSideNav={true}
                  userSettings={props.userSettings}
                />
              </ProtectedElement>
            }
          />
          <Route
            path="/internal/rfi1"
            element={
              <ProtectedElement
                allowed={env.mfelcm.mvs_rfi_ad_group}
                renderUnauthorized={() => (
                  <Navigate to="/unauthorized" replace={true} />
                )}
              >
                <LayoutPlaceholder
                  component={
                    <RFILanding
                      isReadonly={true}
                      userSettings={props.userSettings}
                    />
                  }
                  isReadonly={true}
                  showSideNav
                  userSettings={props.userSettings}
                />
              </ProtectedElement>
            }
          />
          <Route
            index
            path="/internal/rfi2"
            element={
              <ProtectedElement
                allowed={env.mfelcm.mvs_rfi_ad_group}
                renderUnauthorized={() => (
                  <Navigate to="/unauthorized" replace={true} />
                )}
              >
                <LayoutPlaceholder
                  component={
                    <RFILanding
                      isReadonly={true}
                      rfiType={RFI_TYPE_LOCATION}
                      userSettings={props.userSettings}
                    />
                  }
                  isReadonly={true}
                  showSideNav
                  userSettings={props.userSettings}
                />
              </ProtectedElement>
            }
          />

          <Route
            index
            path="internal/rfi3"
            element={
              <ProtectedElement
                allowed={env.mfelcm.mvs_rfi_ad_group}
                renderUnauthorized={() => (
                  <Navigate to="/unauthorized" replace={true} />
                )}
              >
                <RFI3Landing userSettings={props.userSettings} />
              </ProtectedElement>
            }
          />
        </Routes>
      ) : (
        <SpinnerComponent />
      )}
    </ErrorBoundary>
  )
}

MainRouter.propTypes = {
  updateUserSettings: func,
  userSettings: array,
}

function mapStateToProps(state) {
  return { userSettings: state.reducer.userSettings }
}

export const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateUserSettings,
    },
    dispatch,
  )

export default withEnv()(
  compose(connect(mapStateToProps, mapDispatchToProps))(withAuth()(MainRouter)),
)
